/**
 * Smart Assessment Templates
 *
 * Pre-built assessment templates that HR can one-click deploy.
 * Each template defines an assessment (title, description) plus
 * an array of questions that match the backend QuestionModel shape
 * so they can be POSTed directly to /assessments/:id/question/create.
 */

// Matches backend QuestionType enum (db/models/questionsModels.ts)
export enum TemplateQuestionType {
  TextInput = 0,
  FileUpload = 1,
  MultipleChoice = 2,
  SingleChoice = 3,
}

export interface TemplatePossibleChoice {
  option: string;
  isCorrect: boolean;
}

export interface TemplateQuestion {
  text: string;
  type: TemplateQuestionType;
  maxGrade: number;
  possibleChoices?: TemplatePossibleChoice[];
}

export interface AssessmentTemplate {
  id: string; // client-side identifier (slug)
  title: string;
  description: string;
  icon: string; // emoji
  color: string; // accent color for the card
  category: string;
  questionCount: number;
  isOptional: boolean;
  questions: TemplateQuestion[];
}

// ─── Template Definitions ──────────────────────────────────────────

export const ASSESSMENT_TEMPLATES: AssessmentTemplate[] = [
  // ━━━ 1. General Screening ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    id: 'general-screening',
    title: 'General Screening',
    description:
      'A quick-filter assessment to evaluate candidate motivation, relevant experience, availability, work authorization, and salary expectations.',
    icon: '🔍',
    color: '#4A90D9',
    category: 'Screening',
    questionCount: 5,
    isOptional: false,
    questions: [
      {
        text: 'Why are you interested in this role?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 5,
        possibleChoices: [
          { option: 'Career growth and professional development', isCorrect: false },
          { option: 'Passion for this field or industry', isCorrect: false },
          { option: 'Company culture and values align with mine', isCorrect: false },
          { option: 'Looking for new challenges and opportunities', isCorrect: false },
          { option: 'Compensation and benefits package', isCorrect: false },
        ],
      },
      {
        text: 'Describe a relevant experience that prepares you for this position.',
        type: TemplateQuestionType.TextInput,
        maxGrade: 10,
      },
      {
        text: 'What is your earliest available start date?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 5,
        possibleChoices: [
          { option: 'Immediately', isCorrect: false },
          { option: 'Within 2 weeks', isCorrect: false },
          { option: 'Within 1 month', isCorrect: false },
          { option: 'More than 1 month', isCorrect: false },
        ],
      },
      {
        text: 'Are you legally authorized to work in Canada?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Yes, I am a Canadian citizen', isCorrect: true },
          { option: 'Yes, I am a permanent resident', isCorrect: true },
          { option: 'Yes, I hold a valid work permit', isCorrect: true },
          { option: 'No, I require employer sponsorship', isCorrect: false },
        ],
      },
      {
        text: 'What are your salary expectations for this role? (CAD per year)',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 5,
        possibleChoices: [
          { option: '$30,000 – $40,000', isCorrect: false },
          { option: '$40,000 – $55,000', isCorrect: false },
          { option: '$55,000 – $70,000', isCorrect: false },
          { option: '$70,000+', isCorrect: false },
          { option: 'Negotiable / Open to discussion', isCorrect: false },
        ],
      },
    ],
  },

  // ━━━ 2. Communication Assessment ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    id: 'communication',
    title: 'Communication Assessment',
    description:
      'Evaluates professional writing, clarity of expression, and the ability to communicate effectively in workplace scenarios.',
    icon: '✍️',
    color: '#E67E22',
    category: 'Communication',
    questionCount: 4,
    isOptional: false,
    questions: [
      {
        text: 'You need to inform your manager that a project deadline will be missed by one week. Write a professional email explaining the situation, the reason for the delay, and a proposed revised timeline.',
        type: TemplateQuestionType.TextInput,
        maxGrade: 25,
      },
      {
        text: 'A client sends a frustrated email complaining about a delayed delivery. Write a professional and empathetic response that acknowledges the issue and outlines next steps.',
        type: TemplateQuestionType.TextInput,
        maxGrade: 25,
      },
      {
        text: 'Which of the following is the most professionally appropriate way to begin an email to a colleague you have not met before?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Hey, what\'s up?', isCorrect: false },
          { option: 'Dear Sir/Madam,', isCorrect: false },
          { option: 'Hi [Name], I hope this message finds you well.', isCorrect: true },
          { option: 'To whom it may concern,', isCorrect: false },
        ],
      },
      {
        text: 'Read the following paragraph and rewrite it in 2–3 clear, concise sentences:\n\n"The meeting that was scheduled for tomorrow which was supposed to discuss the quarterly results and also the new marketing strategy that the team has been working on for the past few weeks has been moved to next Monday because several key stakeholders are not available due to a conflict with another important meeting that was just added to the calendar."',
        type: TemplateQuestionType.TextInput,
        maxGrade: 20,
      },
    ],
  },

  // ━━━ 3. Situational Assessment ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    id: 'situational',
    title: 'Situational Assessment',
    description:
      'Tests decision-making, prioritization, and professional judgment through realistic workplace scenarios.',
    icon: '🧩',
    color: '#2C3E50',
    category: 'Situational',
    questionCount: 4,
    isOptional: false,
    questions: [
      {
        text: 'You are working on two tasks with the same deadline. Task A is nearly complete and low-impact. Task B is only 50% done but is critical for a client presentation tomorrow. What do you do?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Finish Task A first since it is almost done, then move to Task B', isCorrect: false },
          { option: 'Prioritize Task B because it is client-critical, and finish Task A afterward if time permits', isCorrect: true },
          { option: 'Ask your manager to reassign one of the tasks to someone else', isCorrect: false },
          { option: 'Work on both simultaneously, switching between them', isCorrect: false },
        ],
      },
      {
        text: 'During a team meeting, a colleague presents an idea that you believe has a significant flaw. How do you handle it?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Stay silent to avoid conflict and address it privately later', isCorrect: false },
          { option: 'Point out the flaw directly in front of everyone so the team is aware', isCorrect: false },
          { option: 'Acknowledge the idea\'s strengths, then raise your concern as a question for the team to discuss', isCorrect: true },
          { option: 'Wait until the idea fails, then explain what went wrong', isCorrect: false },
        ],
      },
      {
        text: 'You receive an urgent request from a senior executive while you are in the middle of a time-sensitive task for your direct manager. What is your best course of action?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Drop your current task immediately and handle the executive\'s request', isCorrect: false },
          { option: 'Ignore the executive\'s request and finish your current task first', isCorrect: false },
          { option: 'Acknowledge the executive\'s request, quickly inform your manager of the conflict, and agree on priorities together', isCorrect: true },
          { option: 'Delegate the executive\'s request to a teammate without informing your manager', isCorrect: false },
        ],
      },
      {
        text: 'You are assigned a new project but the requirements are unclear and the stakeholder is unavailable for the next 3 days. What do you do?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Wait for the stakeholder to return before starting any work', isCorrect: false },
          { option: 'Begin working based on your best assumptions without documenting them', isCorrect: false },
          { option: 'Review any existing documentation, draft clarifying questions, and start on the parts you are confident about while documenting your assumptions', isCorrect: true },
          { option: 'Escalate the situation to your manager and request a deadline extension', isCorrect: false },
        ],
      },
    ],
  },

  // ━━━ 4. Subsidy Eligibility ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    id: 'subsidy-eligibility',
    title: 'Subsidy Eligibility',
    description:
      'Determines if the applicant qualifies for Canadian government wage-subsidy programs based on GPA, residency status, and enrollment.',
    icon: '📋',
    color: '#27AE60',
    category: 'Eligibility',
    questionCount: 4,
    isOptional: false,
    questions: [
      {
        text: 'What is your current cumulative GPA? (on a 4.0 scale)',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 5,
        possibleChoices: [
          { option: '3.5 – 4.0', isCorrect: true },
          { option: '3.0 – 3.49', isCorrect: true },
          { option: '2.5 – 2.99', isCorrect: false },
          { option: 'Below 2.5', isCorrect: false },
        ],
      },
      {
        text: 'Are you a Canadian citizen or permanent resident?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Yes, I am a Canadian citizen', isCorrect: true },
          { option: 'Yes, I am a permanent resident', isCorrect: true },
          { option: 'No', isCorrect: false },
        ],
      },
      {
        text: 'Are you currently enrolled in a recognized Canadian post-secondary institution?',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Yes, enrolled full-time', isCorrect: true },
          { option: 'Yes, enrolled part-time', isCorrect: true },
          { option: 'No, but I recently graduated (within 12 months)', isCorrect: true },
          { option: 'No', isCorrect: false },
        ],
      },
      {
        text: 'What is your expected or actual graduation date? (e.g. June 2026)',
        type: TemplateQuestionType.TextInput,
        maxGrade: 5,
      },
    ],
  },

  // ━━━ 5. Technical – HackerRank ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    id: 'technical-hackerrank',
    title: 'Technical Assessment (HackerRank)',
    description:
      'A timed, link-based technical skills assessment powered by HackerRank. IMPORTANT: When creating your HackerRank test, enable the built-in time limit (recommended: 60–90 minutes) and proctoring features to prevent cheating. Applicants must complete the test in one sitting.',
    icon: '💻',
    color: '#8E44AD',
    category: 'Technical',
    questionCount: 3,
    isOptional: false,
    questions: [
      {
        text: '⏱️ TIMED ASSESSMENT — You must complete the HackerRank test in one sitting within the time limit set by the hiring team. Do NOT open the link until you are ready. Using the link provided by your hiring manager, complete the technical assessment. Once finished, paste the FULL URL of your completed assessment results below.',
        type: TemplateQuestionType.TextInput,
        maxGrade: 80,
      },
      {
        text: 'What date and time (with timezone) did you start and finish the HackerRank assessment? (e.g. "Started: Apr 7, 2026 2:00 PM EDT — Finished: Apr 7, 2026 3:15 PM EDT")',
        type: TemplateQuestionType.TextInput,
        maxGrade: 10,
      },
      {
        text: 'I confirm that I completed this technical assessment independently, without assistance from others or unauthorized resources, within the allotted time limit.',
        type: TemplateQuestionType.SingleChoice,
        maxGrade: 10,
        possibleChoices: [
          { option: 'Yes, I confirm — I completed this assessment independently and within the time limit', isCorrect: true },
          { option: 'No, I received external assistance', isCorrect: false },
        ],
      },
    ],
  },
];
